import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

export default function PdfViewer({ fileBlob }) {
  const containerRef = useRef(null);
  const canvasRefs = useRef([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function renderAllPages() {
      setLoading(true);
      setError(null);
      try {
        const arrayBuffer = await fileBlob.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) return;
        
        const numPages = pdf.numPages;
        setTotalPages(numPages);
        
        // Aguarda os canvas serem montados
        await new Promise((r) => requestAnimationFrame(r));
        
        const containerWidth = containerRef.current?.clientWidth || 800;
        
        // Renderiza todas as páginas
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          if (cancelled) break;
          
          const page = await pdf.getPage(pageNum);
          const canvas = canvasRefs.current[pageNum - 1];
          
          if (!canvas) continue;
          
          const ctx = canvas.getContext("2d");
          const viewport = page.getViewport({ scale: 1 });
          const scale = Math.min(containerWidth / viewport.width, 1.5);
          const scaledViewport = page.getViewport({ scale });
          
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;
          
          await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
        }
      } catch (e) {
        console.error(e);
        setError("Falha ao renderizar PDF");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (fileBlob) {
      renderAllPages();
    }

    return () => {
      cancelled = true;
    };
  }, [fileBlob]);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-auto">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
          <div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-transparent rounded-full" />
        </div>
      )}
      {error && (
        <div className="absolute top-4 left-4 right-4 text-sm text-red-600 bg-red-50 p-3 rounded-md">
          {error}
        </div>
      )}
      <div className="space-y-4 p-4">
        {Array.from({ length: totalPages || 1 }).map((_, index) => (
          <canvas
            key={index}
            ref={(el) => (canvasRefs.current[index] = el)}
            className="block mx-auto shadow-md"
          />
        ))}
      </div>
    </div>
  );
}
